import http, { type HttpResponse, type PaginateConfig } from './http';
import { getCurrentTenantId } from 'src/stores/tenant';
import { getRuntimeConfig } from 'src/utils/runtimeConfig';
import { getAuthToken, getEntraAccessToken } from './authTokenHttp';
import { getMsalInstance } from 'src/auth/msalInstanceManager';
import { getMicrosoftGraphScopes } from 'src/auth/msalConfig';
import { useMagicLink } from 'src/stores/magicLink';
import { MosError } from 'src/utils/errors/MosError';

/**
 * Authentication headers required for all API requests
 */
export interface AuthHeaders {
  Authorization: string;
  'auth-provider': 'entra' | 'keycloak' | 'magic-link'; // entra = Microsoft Entra ID
  'tenant-id'?: string;
}

/**
 * Error thrown when authentication token cannot be obtained
 */
export class AuthTokenError extends Error {
  constructor(
    message: string,
    public readonly provider: string,
  ) {
    super(message);
    this.name = 'AuthTokenError';
  }
}

/**
 * Get access token for Microsoft Graph API using dynamic scope configuration
 * Uses Microsoft Graph scopes from VITE_ENTRA_SCOPES environment variable
 * This ensures the token has the correct audience (https://graph.microsoft.com)
 * for Microsoft Graph API calls, separate from backend API tokens
 */
export const getEntraAccessTokenForMicrosoftGraph = async (): Promise<
  string | undefined
> => {
  try {
    const msalInstance = getMsalInstance();
    const accounts = msalInstance.getAllAccounts();
    const account = msalInstance.getActiveAccount() || accounts[0];

    if (!account) {
      return undefined;
    }

    const graphScopes = getMicrosoftGraphScopes();

    const response = await msalInstance.acquireTokenSilent({
      scopes: graphScopes,
      account,
    });
    return response.accessToken;
  } catch (err) {
    if (
      err instanceof Error &&
      err.message.includes('uninitialized_public_client_application')
    ) {
      return undefined;
    }
    return undefined;
  }
};

/**
 * Gets the appropriate access token for backend API
 * Priority: 1. Magic Link (if authenticated), 2. AUTH_PROVIDER config
 * @returns Promise<string> The access token
 * @throws AuthTokenError if token cannot be obtained
 */
const getAccessToken = async (): Promise<string> => {
  // First, check if user is authenticated via magic link (regardless of AUTH_PROVIDER)
  const magicLinkState = useMagicLink.getState();
  if (magicLinkState.authenticated && magicLinkState.accessToken) {
    return magicLinkState.accessToken;
  }

  // If not authenticated via magic link, use the configured AUTH_PROVIDER
  const config = getRuntimeConfig();
  const authProvider: string =
    (config.VITE_AUTH_PROVIDER as string) || 'keycloak';

  try {
    let token: string | undefined;

    if (authProvider === 'entra') {
      token = await getEntraAccessToken();
    } else {
      token = await getAuthToken();
    }

    if (!token) {
      throw new AuthTokenError(
        `Failed to obtain access token for provider: ${authProvider}`,
        authProvider,
      );
    }
    return token;
  } catch (error) {
    if (error instanceof AuthTokenError) {
      throw error;
    }
    throw new AuthTokenError(
      `Failed to obtain access token for provider: ${authProvider}`,
      authProvider,
    );
  }
};

/**
 * Constructs authentication headers for API requests
 * Priority: 1. Magic Link (if authenticated), 2. AUTH_PROVIDER config
 * @returns Promise<AuthHeaders> The authentication headers
 * @throws AuthTokenError if headers cannot be constructed
 */
export const getAuthHeaders = async (): Promise<AuthHeaders> => {
  // First, check if user is authenticated via magic link (regardless of AUTH_PROVIDER)
  const magicLinkState = useMagicLink.getState();
  let authProvider: 'entra' | 'keycloak' | 'magic-link';

  if (magicLinkState.authenticated && magicLinkState.accessToken) {
    authProvider = 'magic-link';
  } else {
    // If not authenticated via magic link, use the configured AUTH_PROVIDER
    const config = getRuntimeConfig();
    authProvider = ((config.VITE_AUTH_PROVIDER as string) || 'keycloak') as
      | 'entra'
      | 'keycloak';
  }

  const token = await getAccessToken();

  const headers: AuthHeaders = {
    Authorization: `Bearer ${token}`,
    'auth-provider': authProvider,
  };

  return headers;
};

/**
 * Enhanced HTTP client that automatically includes authentication headers
 * @param url The API endpoint URL
 * @param config HTTP configuration options
 * @returns Promise<HttpResponse<T>> The HTTP response
 * @throws AuthTokenError if authentication headers cannot be obtained
 */
export const enhancedAuthTokenHttp = async <T>(
  url: string,
  config: PaginateConfig = {},
): Promise<HttpResponse<T>> => {
  let authHeaders: AuthHeaders;

  try {
    authHeaders = await getAuthHeaders();
  } catch (error) {
    if (error instanceof AuthTokenError) {
      // Re-throw auth errors for proper handling upstream
      throw error;
    }

    // For other errors during auth header creation, wrap them in AuthTokenError
    throw new AuthTokenError(
      `Failed to obtain authentication headers: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'unknown',
    );
  }

  try {
    return await http<T>(url, {
      ...config,
      headers: {
        ...config.headers,
        ...authHeaders,
      },
    });
  } catch (error) {
    if (error instanceof MosError) {
      throw error;
    }
    // For HTTP errors, wrap them in AuthTokenError for consistency
    throw new AuthTokenError(
      `Failed to make authenticated request: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'unknown',
    );
  }
};

/**
 * Enhanced HTTP client for Microsoft Graph API calls
 * Uses Microsoft Graph specific tokens and enhanced headers
 * @param url The Microsoft Graph API endpoint URL
 * @param config HTTP configuration options
 * @returns Promise<HttpResponse<T>> The HTTP response
 * @throws AuthTokenError if authentication headers cannot be obtained
 */
export const enhancedMicrosoftGraphHttp = async <T>(
  url: string,
  config: PaginateConfig = {},
): Promise<HttpResponse<T>> => {
  const runtimeConfig = getRuntimeConfig();
  const authProvider =
    (runtimeConfig.VITE_AUTH_PROVIDER as string) || 'keycloak';

  if (authProvider !== 'entra') {
    throw new AuthTokenError(
      'Microsoft Graph API calls are only available with Entra ID authentication',
      authProvider,
    );
  }

  try {
    const token = await getEntraAccessTokenForMicrosoftGraph();
    if (!token) {
      throw new AuthTokenError(
        'Failed to obtain Microsoft Graph access token',
        authProvider,
      );
    }

    const headers: AuthHeaders = {
      Authorization: `Bearer ${token}`,
      'auth-provider': 'entra',
    };

    const tenantId = getCurrentTenantId();
    if (tenantId) {
      headers['tenant-id'] = tenantId;
    }

    return await http<T>(url, {
      ...config,
      headers: {
        ...config.headers,
        ...headers,
      },
    });
  } catch (error) {
    if (error instanceof AuthTokenError) {
      throw error;
    }
    throw new AuthTokenError(
      `Failed to make Microsoft Graph API request: ${error instanceof Error ? error.message : 'Unknown error'}`,
      authProvider,
    );
  }
};

/**
 * Fallback to existing authTokenHttp for Keycloak authentication
 * This maintains backward compatibility while we transition to enhanced headers
 */
export const getCompatibleAuthTokenHttp = async () => {
  const config = getRuntimeConfig();
  const authProvider = (config.VITE_AUTH_PROVIDER as string) || 'keycloak';

  if (authProvider === 'entra') {
    return enhancedAuthTokenHttp;
  } else {
    const { authTokenHttp } = await import('./authTokenHttp');
    return authTokenHttp;
  }
};
